import { useEffect, useState } from 'react';
import {
  useCustom,
  useList,
  useTranslate,
  useWarnAboutChange,
} from '@refinedev/core';
import {
  Alert,
  Badge,
  Card,
  Col,
  Flex,
  Row,
  Skeleton,
  Space,
  Tabs,
  Tag,
  Typography,
  theme,
} from 'antd';
import { ExportOutlined, InfoCircleOutlined } from '@ant-design/icons';
import type {
  CategoryListOut,
  CuratedSurfaceLineupOut,
} from '@/api/generated/schemas';
import { DisplayMode } from '@/api/generated/schemas';
import { HomePreview } from '@/components/user_catalog/home_page/HomePreview';
import { HeroTab, HERO_URL } from './HeroTab';
import { SectionsTab } from './SectionsTab';
import { sectionRowsFromRecord } from '@/components/user_catalog/sections/sectionRows';
import { useShowTab } from '@/components/common/show/useShowTab';
import { findRootCategory } from '../findRootCategory';
import { heroRowsToPublicationIds, heroSlotsToRows } from '../heroLineup';
import {
  isHeroDirty,
  isSectionsDirty,
  type HeroDraft,
  type SectionsDraft,
} from '../homeDraft';
import { resolveHomePreview } from '../resolveHomePreview';

const TABS = ['hero', 'sections'] as const;

export const HomePageEdit = () => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const [activeTab, onTabChange] = useShowTab(TABS, 'hero');
  const { setWarnWhen } = useWarnAboutChange();
  // Drafts live above the tabs so an unsaved edit survives a tab switch.
  const [heroDraft, setHeroDraft] = useState<HeroDraft>();
  const [sectionsDraft, setSectionsDraft] = useState<SectionsDraft>();

  const { result, query } = useList<CategoryListOut>({
    resource: 'user_catalog.categories',
    filters: [{ field: 'parent_id', operator: 'null', value: true }],
    pagination: { pageSize: 50 },
  });
  const root = findRootCategory(result?.data);

  const { query: heroQuery } = useCustom<CuratedSurfaceLineupOut>({
    url: HERO_URL,
    method: 'get',
  });
  const lineup = heroQuery.data?.data;
  const slots = heroSlotsToRows(lineup?.slots);

  const serverHero: HeroDraft = {
    name: root?.name,
    description: root?.description,
    publication_ids: heroRowsToPublicationIds(slots),
  };
  const heroDirty = isHeroDirty(heroDraft, serverHero);

  // Rows edit in section order regardless of server row order, and carry
  // their display mode — the tab PATCHes the whole list, so dropping it here
  // would reset every section to regular on any save.
  const sectionRows = sectionRowsFromRecord(root?.sections);
  const serverSections: SectionsDraft = {
    rows: sectionRows.map((row) => ({
      collection_id: row.collection_id as number,
      display_mode: row.display_mode ?? DisplayMode.regular,
    })),
  };
  const sectionsDirty = isSectionsDirty(sectionsDraft, serverSections);

  const preview = resolveHomePreview(
    { hero: serverHero, sections: serverSections },
    { hero: heroDraft, sections: sectionsDraft }
  );

  // Feeds Refine's leave-the-page guard, which the tabs' own forms no longer
  // arm for us.
  const anyDirty = heroDirty || sectionsDirty;
  useEffect(() => {
    setWarnWhen(anyDirty);
    return () => setWarnWhen(false);
  }, [anyDirty, setWarnWhen]);

  const leavingTabIsDirty = activeTab === 'hero' ? heroDirty : sectionsDirty;
  const confirmTabChange = (key: string) => {
    if (leavingTabIsDirty && !window.confirm(t('warnWhenUnsavedChanges')))
      return;
    onTabChange(key);
  };

  // Counts follow the draft so they move with an unsaved edit.
  const heroCount =
    heroDraft?.publication_ids.length ?? serverHero.publication_ids.length;
  const sectionsCount = sectionsDraft?.rows.length ?? sectionRows.length;

  const tabLabel = (
    key: (typeof TABS)[number],
    dirty: boolean,
    count: number
  ) => (
    <Badge dot={dirty} color={token.colorWarning} offset={[6, -2]}>
      <Space size={token.marginXXS}>
        {t(`user_catalog.home_page.tabs.${key}`)}
        <Tag style={{ marginInlineEnd: 0 }}>{count}</Tag>
      </Space>
    </Badge>
  );

  if (query.isLoading || heroQuery.isLoading) {
    return (
      <Card title={t('user_catalog.home_page.title')}>
        <Skeleton active paragraph={{ rows: 3 }} />
      </Card>
    );
  }

  if (!root) {
    return (
      <Card title={t('user_catalog.home_page.title')}>
        <Alert
          type="error"
          showIcon
          message={t('user_catalog.home_page.root_missing.title')}
          description={t('user_catalog.home_page.root_missing.description')}
        />
      </Card>
    );
  }

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} xl={14}>
        <Card title={t('user_catalog.home_page.title')}>
          {/* This screen has no record of its own — it edits the root
              category, and says so rather than hiding it. */}
          <Flex
            gap={token.marginXS}
            align="flex-start"
            style={{ marginBottom: token.marginSM }}
          >
            <InfoCircleOutlined
              style={{ color: token.colorTextTertiary, marginTop: 4 }}
            />
            <Typography.Text type="secondary">
              {t('user_catalog.home_page.root_notice.text')}{' '}
              <Typography.Link
                href={`/user_catalog/categories/${root.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('user_catalog.home_page.root_notice.link')}
                <ExportOutlined style={{ marginInlineStart: 4 }} />
              </Typography.Link>
            </Typography.Text>
          </Flex>

          <Tabs
            activeKey={activeTab}
            onChange={confirmTabChange}
            items={[
              {
                key: 'hero',
                label: tabLabel('hero', heroDirty, heroCount),
                children: (
                  <HeroTab
                    rootId={root.id}
                    initial={{
                      name: root.name,
                      description: root.description,
                      slots,
                    }}
                    version={lineup?.version ?? 0}
                    dirty={heroDirty}
                    onDraftChange={setHeroDraft}
                    onLineupSaved={() => void heroQuery.refetch()}
                  />
                ),
              },
              {
                key: 'sections',
                label: tabLabel('sections', sectionsDirty, sectionsCount),
                children: (
                  <SectionsTab
                    rootId={root.id}
                    sections={sectionRows}
                    dirty={sectionsDirty}
                    onDraftChange={setSectionsDraft}
                  />
                ),
              },
            ]}
          />
        </Card>
      </Col>

      <Col xs={24} xl={10}>
        <div style={{ position: 'sticky', top: token.marginLG }}>
          <HomePreview model={preview} />
        </div>
      </Col>
    </Row>
  );
};
